import { safeConsoleError } from '../utils/safeConsole';

// API Configuration
export const API_BASE_URL = 'https://api.oecmr.com';

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  given_name?: string;
  family_name?: string;
  created_at?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  given_name: string;
  family_name: string;
}

export interface AuthResponse {
  message: string;
  access_token: string;
  id_token: string;
  user: User;
  note?: string;
}

export interface RegisterResponse {
  message: string;
  user_id?: string;
  note?: string;
}

export interface PasswordResetRequestResponse {
  message: string;
}

export interface ConfirmPasswordResetData {
  email: string;
  code: string;
  new_password: string;
}

export interface ConfirmPasswordResetResponse {
  message: string;
}

// Local Storage Keys
const ID_TOKEN_KEY = 'oecmr_id_token';
const ACCESS_TOKEN_KEY = 'oecmr_access_token';
const USER_KEY = 'oecmr_user';
const AUTH_ERROR_MESSAGES = {
  login: 'No se pudo iniciar sesion. Revisa las credenciales o usa restablecer contrasena.',
  register: 'No se pudo crear la cuenta. Si el usuario ya existe, inicia sesion o restablece la contrasena desde el login.',
  passwordResetRequest: 'No se pudo iniciar el restablecimiento. Verifica el correo e intentalo de nuevo.',
  passwordResetConfirm: 'No se pudo confirmar el restablecimiento. Verifica el codigo e intentalo de nuevo.',
  currentUser: 'No se pudo obtener la informacion del usuario.',
};

class AuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  // API Calls
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        throw new Error(AUTH_ERROR_MESSAGES.login);
      }

      const data: AuthResponse = await response.json();
      if (
        typeof data.id_token !== 'string' || !data.id_token ||
        typeof data.access_token !== 'string' || !data.access_token
      ) {
        throw new Error(AUTH_ERROR_MESSAGES.login);
      }
      
      // The ID token is the API Gateway bearer; the access token is only for Cognito operations.
      this.setToken(data.id_token);
      this.setAccessToken(data.access_token);
      this.setUser(data.user);
      
      return data;
    } catch (error) {
      safeConsoleError('Login error', 'auth.login');
      throw error;
    }
  }

  async register(userData: RegisterData): Promise<RegisterResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error(AUTH_ERROR_MESSAGES.register);
      }

      const data: RegisterResponse = await response.json();
      return data;
    } catch (error) {
      safeConsoleError('Register error', 'auth.register');
      throw error;
    }
  }

  async requestPasswordReset(email: string): Promise<PasswordResetRequestResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (!response.ok) {
        throw new Error(AUTH_ERROR_MESSAGES.passwordResetRequest);
      }

      return await response.json();
    } catch (error) {
      safeConsoleError('Password reset request error', 'auth.password_reset.request');
      throw error;
    }
  }

  async confirmPasswordReset(data: ConfirmPasswordResetData): Promise<ConfirmPasswordResetResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/confirm-forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email.trim().toLowerCase(),
          code: data.code.trim(),
          new_password: data.new_password,
        }),
      });

      if (!response.ok) {
        throw new Error(AUTH_ERROR_MESSAGES.passwordResetConfirm);
      }

      return await response.json();
    } catch (error) {
      safeConsoleError('Password reset confirmation error', 'auth.password_reset.confirm');
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const token = this.getAccessToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await fetch(`${this.baseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(AUTH_ERROR_MESSAGES.currentUser);
      }

      const data = await response.json();
      this.setUser(data.user);
      return data.user;
    } catch (error) {
      safeConsoleError('Get current user error', 'auth.current_user');
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      const token = this.getAccessToken();
      if (token) {
        // Call logout endpoint (optional for mock API)
        await fetch(`${this.baseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      safeConsoleError('Logout error', 'auth.logout');
    } finally {
      // Always clear local storage
      this.clearAuthData();
    }
  }

  // Token Management
  setToken(token: string): void {
    localStorage.setItem(ID_TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(ID_TOKEN_KEY);
  }

  private setAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  private getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  // User Management
  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  getUser(): User | null {
    const userData = localStorage.getItem(USER_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  // Auth State
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  clearAuthData(): void {
    localStorage.removeItem(ID_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  // API Request Helper with Auth
  async authenticatedRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    // If unauthorized, clear auth data
    if (response.status === 401) {
      this.clearAuthData();
      throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
    }

    return response;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService; 
